<script setup>
  import { ref, watch, watchEffect, onMounted } from 'vue';
  import BaseModal from '@/components/base/BaseModal.vue';
  import Icon from "@/components/icons/Icon.vue";
  import PopupMenuFavoriteItemActions from '@/components/PopupMenuFavoriteItemActions.vue';
  import FloatingMenuFavoriteItemActions from '@/components/FloatingMenuFavoriteItemActions.vue';
  import ModalMultiSelectActions from '@/components/ModalMultiSelectActions.vue';
  import CheckboxControl from '@/components/CheckboxControl.vue';
  import draggable from 'vuedraggable'

  const emits = defineEmits(['close', 'sortEnd']);
  const selectedItemIds = defineModel({ required: true, default: () => [] });

  const props = defineProps({
    items: {
      type: Array,
      required: true  
    },
    idField: {
      type: String,
      required: true
    },
    nameField: {
      type: String,
      required: true
    },
    title: {
      type: String,
      required: true
    },
    hideActions: {
      type: Array,
      default: () => []
    },
    dragDisabled: {
      type: Boolean,
      default: false
    },
    show: {
      type: Boolean,
      default: false
    }
  })

  const items = ref(props.items);
  const showModal = ref(false);
  const showRenameModal = ref(false);
  const showDeleteModal = ref(false);
  const showCopyModal = ref(false);
  const editItemId = ref(null);

  const showMoveCopyModal = ref(false);
  const moveCopyAction = ref(null);
  const mode = ref('manage'); // 'manage' or 'multi-select'

  function toggleMode() {
    mode.value = mode.value === 'manage' ? 'multi-select' : 'manage';
    selectedItemIds.value = [];
  }

  function closeModal() {
    showModal.value = false;

    emits('close');
  }

  function closeSecondaryModals() {
    showRenameModal.value = false;
    showDeleteModal.value = false;
    showCopyModal.value = false;
    showMoveCopyModal.value = false;

    deselectAllItems();
  }

  function selectAllItems() {
    selectedItemIds.value = items.value.map(item => item[props.idField]);
  }

  function deselectAllItems() {
    selectedItemIds.value = [];
  }

  function handleProfileManagementActionClick(itemId, actionType) {
    editItemId.value = itemId;
    // The delete, move, and copy modals takes an array. 
    // Move and copy ids array we be converted to an array of containers for the modal
    // Delete can just take the single container id as is, so we still need to set it as an array here.
    selectedItemIds.value = [itemId];

    if (actionType === 'rename') {
      showRenameModal.value = true;
    } else if (actionType === 'delete') {
      showDeleteModal.value = true;
    } else if (actionType === 'move' || actionType === 'copy') {
      moveCopyAction.value = actionType;
      showMoveCopyModal.value = true;
    }
  }

  async function handleSortEnd() {
    emits('sortEnd', items.value);
  }

  function handleMultiSelectClick(action) {
    if (action === 'move' || action === 'copy') {
      moveCopyAction.value = action;
      showMoveCopyModal.value = true;
    }
    else if (action === 'delete') { 
      showDeleteModal.value = true;
    }
  }
  
  watchEffect(() => showModal.value = props.show);
  watchEffect(() => items.value = props.items);
</script>

<template>
  <BaseModal
    v-model="showModal"
    :allowOutsideClick="true"
    height="tall"
    @close="closeModal"
  >
  <header class="jwx-modal__header">
    <h2 class="jwx-modal__title">{{ props.title }}</h2>
    </header>          
    <div class="jwx-modal__body" style="position: relative;">
      <div class="jwx-modal__section">
        <slot name="description" />
      </div>
      <div class="jwx-modal__section">
        <ModalMultiSelectActions
          :mode="mode"
          @selectAll="selectAllItems"
          @clearAll="deselectAllItems"
          @toggleMode="toggleMode"
        />
        <draggable
          :list="items"
          :item-key="(item) => item[props.idField]"
          @end="handleSortEnd"
          :disabled="mode === 'multi-select' && props.dragDisabled"
          class="jwx-block jwx-table-view"
          tag="ul"
          handle=".jwx-table-view__drag-handle"
          :animation="200"
          data-surface="1">
            <template #item="{element}">
              <li
                class="jwx-table-view__row"
                :data-draggable-row="mode !== 'multi-select'"
                :key="element[props.idField]"
              >
                <div v-if="mode === 'multi-select'" class="jwx-table-view__row-content" >
                  <div class="jwx-table-view__row-start-block">
                    <CheckboxControl
                      :name="`checkbox-${element[props.idField]}`"
                      :id="`checkbox-${element[props.idField]}`"
                      :value="element[props.idField]"
                      :srLabel="`Select ${element[props.nameField]}`"
                      v-model="selectedItemIds"
                    />
                  </div>
                  
                  <label :for="`checkbox-${element[props.idField]}`" class="jwx-table-view__row-label jwx-font-weight-400">{{ element[props.nameField] }}</label>
                </div>
                <div v-else class="jwx-table-view__row-content">
                  <div v-if="!props.dragDisabled" class="jwx-table-view__start-block jwx-table-view__drag-handle">
                    <Icon name="grip-dots" class="jwx-settings__drag-icon" />
                  </div>
                  
                  <p class="jwx-table-view__row-label jwx-font-weight-400">{{ element[props.nameField] }}</p>

                  <div class="jwx-table-view__row-actions">
                    <PopupMenuFavoriteItemActions
                      :itemId="element[props.idField]"
                      :hideActions="props.hideActions"
                      @rename="handleProfileManagementActionClick(element[props.idField], 'rename')"
                      @copy="handleProfileManagementActionClick(element[props.idField], 'copy')"
                      @move="handleProfileManagementActionClick(element[props.idField], 'move')"
                      @delete="handleProfileManagementActionClick(element[props.idField], 'delete')"
                    />
                  </div>
                </div>
              </li>
            </template>
        </draggable>
      </div>
      <FloatingMenuFavoriteItemActions
        v-if="mode === 'multi-select' && selectedItemIds.length > 0"
        :itemId="selectedItemIds"
        :hideActions="props.hideActions"
        @copy="handleMultiSelectClick('copy')"
        @move="handleMultiSelectClick('move')"
        @delete="handleMultiSelectClick('delete')"
      />
    </div>
    <div class="jwx-modal__footer">
      <div class="jwx-modal__actions">
        <button 
          type="button" 
          @click.prevent="closeModal"
          class="jwx-button jwx-button--success"
        >
          Close
        </button>
      </div>
    </div>
    <slot 
      name="modals" 
      :showRenameModal="showRenameModal" 
      :showDeleteModal="showDeleteModal" 
      :showMoveCopyModal="showMoveCopyModal"
      :moveCopyAction="moveCopyAction"
      :closeSecondaryModals="closeSecondaryModals"
    ></slot>
  </BaseModal>
</template>